import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useSalesContext } from "../../Pages/SalesContext";
import "../../CustomerCoverage/css/CustomerCoverageTable.css";
import "./Dashboard.css";

interface MonthlyData {
  actual_joint_visits(actual_joint_visits: any): unknown;
  calls: number;
  unique_doctors: number;
  planned_date: string | null;
  visit_type: string | null;
  visitdates: number[];
  joint_visits: number;
  AchievedFreq: number;
}

interface ProductDetail {
  product_name: string;
}

interface ActivityItem {
  joint_visit_user_role(joint_visit_user_role: any): unknown;
  activity_name: string;
  monthly_data: Record<string, MonthlyData>;
  total_calls: number;
  unique_doctors: number;
  doctor_visits: number;
  joint_visits: number;
  dms: number;
  total_dms: number;
  day_notes: string;
  product_details: ProductDetail[];
}

interface ActivityMonthData {
  monthName: string;
  AllDaysInMonth: number[];
  data: ActivityItem[];
}

interface DashboardTableProps {
  userId: string;
  activeCard: string;
  selectedMonth: string;
  userRole: string;
}

const DashboardTable: React.FC<DashboardTableProps> = ({
  userId,
  activeCard,
  selectedMonth,
  userRole,
}) => {
  const { kpiData } = useSalesContext();
  const [monthData, setMonthData] = useState<ActivityMonthData[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);


  // useEffect(() => {
  //   if (!kpiData) {
  //     setMonthData(null);
  //     setLoading(false);
  //     return;
  //   }

  //   try {
  //     const monthKey = Object.keys(kpiData).find((key) =>
  //       key.toLowerCase().includes("activity_table")
  //     );

  //     if (monthKey && kpiData[monthKey]) {
  //       setMonthData(kpiData[monthKey]);
  //     } else {
  //       console.warn("⚠️ No activity table found in kpiData");
  //       setMonthData(null);
  //     }
  //   } catch (err) {
  //     console.error("❌ Error extracting month data:", err);
  //     setMonthData(null);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [kpiData]);
useEffect(() => {
  if (!kpiData) {
    setMonthData(null);
    setLoading(false);
    return;
  }

  try {
    // find all months
    const monthKeys = Object.keys(kpiData).filter((key) =>
      key.toLowerCase().startsWith("activity_table_")
    );

    if (monthKeys.length === 0) {
      setMonthData([]);
      setLoading(false);
      return;
    }

    // convert to array of month objects
    const multiMonthData = monthKeys.map((key) => {
      const raw = kpiData[key];
      const monthName = key
        .replace("activity_table_", "")
        .replace("_data", "");

      return {
        monthName,
        AllDaysInMonth: raw.AllDaysInMonth || [],
        data: raw.data || []
      };
    });

    setMonthData(multiMonthData);
  } catch (e) {
    console.error(e);
    setMonthData([]);
  } finally {
    setLoading(false);
  }
}, [kpiData]);


useEffect(() => {
  if (!Array.isArray(monthData) || monthData.length === 0) {
    setTableData([]);
    return;
  }

  const apiYear =
    kpiData?.year?.[0]?.replace("FY", "").trim() ||
    new Date().getFullYear().toString();

  let finalRows: any[] = [];

  monthData.forEach((m) => {
    const monthName = m.monthName;

const rows = m.AllDaysInMonth.map((day: number) => {
  const formattedDate = `${day.toString().padStart(2, "0")} ${monthName} ${apiYear}`;
  const monthIndex = new Date(`${monthName} 1, ${apiYear}`).getMonth();
  const currentRowDate = new Date(apiYear, monthIndex, day);
 const formattedDate2 = `${day.toString().padStart(2, "0")} ${monthName} ${apiYear}`;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (currentRowDate > today) {
    return null;
  }
  const dayData = m.data.flatMap((item: ActivityItem) => {
    if (!item?.activity_name) return [];

    const [_, doctorName = "-", speciality = "-", category = "-"] =
      item.activity_name.split("|").map((s) => s.trim());

    const monthKey = Object.keys(item.monthly_data ?? {})?.[0];
    const visitDetails = item.monthly_data?.[monthKey];

    const isVisited = visitDetails?.visitdates?.includes(day);
    if (!isVisited) return [];

    const visitIndex = visitDetails?.visitdates?.indexOf(day);
    const plannedDate =
      Array.isArray(visitDetails?.planned_date) && visitIndex !== -1
        ? visitDetails.planned_date[visitIndex]
        : null;
// Always take all visit dates from backend
const allVisitDates =
  Array.isArray(visitDetails?.visitdates) &&
  visitDetails.visitdates.length > 0
    ? visitDetails.visitdates.join(", ")
    : "-";

    return {
      Date: formattedDate,
      Planned_Date: plannedDate
        ? new Date(plannedDate).toLocaleDateString("en-GB")
        : "-",

      Doctor_Name: doctorName,
      Category: category,
      Speciality: speciality,
  Visit_Type:
  String(visitDetails?.actual_joint_visits).toLowerCase() === "yes"
    ? `Yes with ${item.joint_visit_user_role}`
    : "No Joint Visits",
     ActualVisitType : 
           String(visitDetails?.joint_visits),
      Achieved_Freq: visitDetails?.AchievedFreq ?? "-",
      Past_Visit_Dates_Month: allVisitDates,
      DMS: item.dms ?? "0",
  day_notes: item.day_notes ?? "-", 
      _visitDetails: visitDetails,
    };
  });

  if (dayData.length === 0) {
    return {
      Month: monthName,
     Day: formattedDate2,
      Activity_Type: "No data Available",
      Joint_FW: "-",
      Total_Doctor_Calls: 0,
      Day_Notes: "-",
      detailData: [],
    };
  }

  const firstVisitDetails = dayData[0]?._visitDetails ?? null;
// Collect all visit types for the day
const visitTypes = dayData
  .map((d: any) => d._visitDetails?.visit_type)
  .filter((v: any) => v && v.trim() !== "");
const finalActivityType =
  visitTypes.length === 0
    ? "-"
    : visitTypes.filter((v: any, i: number, a: any[]) => a.indexOf(v) === i).join(" / ");
// Collect unique day notes for the day

const JFWvisit = dayData
  .map((d: any) => d._visitDetails?.joint_visits)
  .filter((v: any) => v && v.trim() !== "");
const finalJFWvisit =
  JFWvisit.length === 0
    ? "-"
    : JFWvisit.filter((v: any, i: number, a: any[]) => a.indexOf(v) === i).join(" / ");
const dayNotes = Array.from(
  new Set(
    dayData
      .map((d: any) => d.day_notes)
      .filter((n: any) => n && n.trim() !== "-")
  )
);

// Final notes text
const finalDayNotes =
  dayNotes.length > 0 ? dayNotes.join(" / ") : "No data Available";

  return {
    Month: monthName,
    Day: formattedDate2,
Activity_Type:finalActivityType,
    Joint_FW: finalJFWvisit,
    Total_Doctor_Calls: firstVisitDetails?.total_calls ?? dayData.length,
Day_Notes: finalDayNotes ?? "--",
    detailData: dayData,
  };
});

  finalRows.push(...rows.filter(Boolean));
  });

  setTableData(finalRows);
}, [monthData, kpiData]);


  // ✅ Loading
  // ✅ Empty State
  const selectedMonthData =
    monthData && Array.isArray(monthData)
      ? monthData.find((m) => m.monthName === selectedMonth) || monthData[0]
      : null;
const visitsColumnTitle = selectedMonthData
  ? `Visits for ${selectedMonthData.monthName}`
  : `Visits for ${selectedMonth || ""}`;
  if (!selectedMonthData || tableData.length === 0) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="body2" color="textSecondary">
          No activity data available for {selectedMonth}.
        </Typography>
      </Box>
    );
  }

  // ✅ Expand/Collapse
  const toggleRow = (index: number) => {
    setExpandedRows((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  return (
    <> 
    <Box className="Dashboard-Customer-container" alignItems="center" mb={2}>
      {/* <Typography className="Customer-title" variant="h6" textAlign="center">
        Activity Tracker
      </Typography> */}
       <Typography variant="body2" className="Customer-hint" ml={2}>
        Click on any date to view doctor-wise activity details.
      </Typography>
   </Box>

    <Box className="Customer-container" >
        <TableContainer component={Paper} sx={{ mt: 2, overflow: "auto", maxHeight: "70vh" }}>
        <Table
          size="small"
          sx={{
            borderCollapse: "collapse",
            "& td, & th": {
              borderRight: "1px solid #ccc",
              borderBottom: "1px solid #ccc",
              padding: "6px 8px",
            },
            "& th:last-child, & td:last-child": { borderRight: "none" },
          }}
        >
          <TableHead sx={{ backgroundColor: "#c5c9d2ff",  position: "sticky",
    top: "0px", // below main header + title box
    zIndex: 998,
  }}>
            <TableRow>
              <TableCell sx={{ width: "5%", fontWeight: "bold" }}>S.No</TableCell>
              <TableCell sx={{ width: "12%", fontWeight: "bold" }}>Date</TableCell>
              <TableCell sx={{ width: "25%", fontWeight: "bold" }}>Activity Type</TableCell>
              <TableCell sx={{ width: "20%", fontWeight: "bold" }}>Joint FW?</TableCell>
              <TableCell sx={{ width: "10%", fontWeight: "bold" }}>Total Doctor Calls</TableCell>
              <TableCell sx={{ width: "15%", fontWeight: "bold" }}>Title</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {tableData.map((row, index) => (
              <React.Fragment key={index}>
                <TableRow
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#eef1ff" },
                    bgcolor: expandedRows.includes(index)
                      ? "#f8f9ff"
                      : "inherit",
                  }}
                  onClick={() => toggleRow(index)}
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.Day}</TableCell>
                  <TableCell>{row.Activity_Type}</TableCell>
                  <TableCell>{row.Joint_FW}</TableCell>
                  <TableCell>{row.Total_Doctor_Calls}</TableCell>
                  <TableCell>{row.Day_Notes}</TableCell>
                </TableRow>

                {expandedRows.includes(index) && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ padding: 0 }}>
                      <Table
                        size="small"
                        sx={{
                          width: "100%",
                          borderCollapse: "collapse",
                          "& td, & th": {
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            padding: "4px 6px",
                          },
                          "& th:last-child, & td:last-child": {
                            borderRight: "none",
                          },
                        }}
                      >
                        <TableHead sx={{ backgroundColor: "#ffe6e6" 
 }}>
                          <TableRow>
                            <TableCell sx={{ width: "5%" }}>S.No</TableCell>
                            <TableCell sx={{ width: "12%" }}> Date</TableCell>
                            <TableCell sx={{ width: "15%" }}>Doctor Name</TableCell>
                            <TableCell sx={{ width: "10%" }}>Category</TableCell>
                            <TableCell sx={{ width: "10%" }}>Speciality</TableCell>
                            <TableCell sx={{ width: "10%" }}>Visit Type</TableCell>
                            <TableCell sx={{ width: "10%" }}>Achieved / Freq</TableCell>
                            <TableCell sx={{ width: "10%" }}> Visits for {row.Month}</TableCell>
                            {/* <TableCell sx={{ width: "10%" }}>Product Detailed</TableCell> */}
                            {/* <TableCell sx={{ width: "5%" }}>DMS</TableCell> */}
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {row.detailData.map((d: any, i: number) => (
                            <TableRow key={i}>
                                   <TableCell>{i + 1}</TableCell>
                              <TableCell>{d.Planned_Date}</TableCell>
                              <TableCell>{d.Doctor_Name}</TableCell>
                              <TableCell>{d.Category}</TableCell>
                              <TableCell>{d.Speciality}</TableCell>
                              <TableCell>{d.Visit_Type}</TableCell>
                              <TableCell>{d.Achieved_Freq}</TableCell>
                              <TableCell>{d.Past_Visit_Dates_Month}</TableCell>
                              {/* <TableCell>{d.Products_Detailed}</TableCell> */}
                              {/* <TableCell>{d.DMS}</TableCell> */}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer></Box></>
   
 
  );
};

export default DashboardTable;



